import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/Constants';
import { ARENAS, ArenaDefinition } from '../data/Arenas';
import { TorneoConfig } from './MenuScene';
import { PlayerSelection } from './SelectionScene';
import { TorneoSystem, MatchData } from '../systems/TorneoSystem';

export class ArenaSelectScene extends Phaser.Scene {
  private config!: TorneoConfig;
  private selections!: PlayerSelection[];
  private torneoSystem!: TorneoSystem | null;
  private currentMatchData!: MatchData | null;
  private isTorneo: boolean = false;
  private matchSelections!: PlayerSelection[] | null;

  constructor() {
    super({ key: 'ArenaSelectScene' });
  }

  init(data: {
    config: TorneoConfig;
    selections: PlayerSelection[];
    torneoSystem?: TorneoSystem | null;
    currentMatchData?: MatchData | null;
    isTorneo?: boolean;
    matchSelections?: PlayerSelection[];
  }): void {
    this.config = data.config;
    this.selections = data.selections;
    this.torneoSystem = data.torneoSystem || null;
    this.currentMatchData = data.currentMatchData || null;
    this.isTorneo = data.isTorneo || false;
    this.matchSelections = data.matchSelections || null;

    // Si es torneo y no hay match actual, obtener la primera del bracket
    if (this.isTorneo && this.torneoSystem && !this.currentMatchData) {
      this.currentMatchData = this.torneoSystem.getNextMatch();
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#080818');

    // Fondo decorativo
    const bgG = this.add.graphics();
    bgG.lineStyle(1, 0x1a1a4e, 0.2);
    for (let i = 0; i < 15; i++) {
      const y = i * 50;
      bgG.beginPath();
      bgG.moveTo(0, y);
      bgG.lineTo(GAME_WIDTH, y + 30);
      bgG.strokePath();
    }

    // Barra superior
    const titleBar = this.add.graphics();
    titleBar.fillStyle(0x1a1a3e, 0.8);
    titleBar.fillRect(0, 0, GAME_WIDTH, 70);

    this.add.text(GAME_WIDTH / 2, 35, '🌍  SELECCIONA ARENA', {
      fontSize: '28px',
      fontFamily: 'Arial Black, Impact, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5);

    const startX = 80;
    const startY = 100;
    const cardW = 260;
    const cardH = 145;
    const cols = 4;
    const gapX = 20;
    const gapY = 20;

    ARENAS.forEach((arena, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gapX);
      const y = startY + row * (cardH + gapY);

      const container = this.add.container(x, y);

      // Sombra
      const shadow = this.add.rectangle(3, 3, cardW, cardH, 0x000000, 0.3);
      shadow.setOrigin(0, 0);

      // Fondo
      const bg = this.add.rectangle(0, 0, cardW, cardH, 0x0d0d2b, 0.9)
        .setStrokeStyle(2, this.getArenaColor(arena), 0.5)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      container.add(shadow);
      container.add(bg);

      // Color band
      const colorBand = this.add.rectangle(0, 0, cardW, 5, this.getArenaColor(arena), 0.8);
      colorBand.setOrigin(0, 0);
      container.add(colorBand);

      // Preview visual (círculo decorativo)
      const preview = this.add.graphics();
      preview.fillStyle(this.getArenaColor(arena), 0.2);
      preview.fillCircle(cardW / 2, 50, 40);
      preview.lineStyle(3, this.getArenaColor(arena), 0.7);
      preview.strokeCircle(cardW / 2, 50, 40);

      // Efecto interno según arena
      if (arena.id === 'hielo') {
        preview.lineStyle(1, 0xffffff, 0.3);
        for (let s = 0; s < 6; s++) {
          const a = (s / 6) * Math.PI * 2;
          preview.beginPath();
          preview.moveTo(cardW / 2, 50);
          preview.lineTo(cardW / 2 + Math.cos(a) * 35, 50 + Math.sin(a) * 35);
          preview.strokePath();
        }
      } else if (arena.id === 'lava') {
        preview.fillStyle(0xff6600, 0.3);
        preview.fillCircle(cardW / 2 - 10, 50 + 10, 8);
        preview.fillStyle(0xffaa00, 0.3);
        preview.fillCircle(cardW / 2 + 12, 50 - 8, 6);
      } else if (arena.id === 'electro') {
        preview.lineStyle(2, 0xffff00, 0.5);
        for (let s = 0; s < 8; s++) {
          const a = (s / 8) * Math.PI * 2;
          const r2 = s % 2 === 0 ? 35 : 25;
          preview.beginPath();
          preview.moveTo(cardW / 2 + Math.cos(a) * r2, 50 + Math.sin(a) * r2);
          preview.lineTo(cardW / 2 + Math.cos(a) * 40, 50 + Math.sin(a) * 40);
          preview.strokePath();
        }
      }

      container.add(preview);

      // Nombre
      const nameText = this.add.text(cardW / 2, 95, arena.name, {
        fontSize: '17px',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(nameText);

      // Descripción
      const descText = this.add.text(cardW / 2, 118, arena.description.substring(0, 35) + '...', {
        fontSize: '11px',
        color: '#bdc3c7',
        align: 'center',
      }).setOrigin(0.5);
      container.add(descText);

      container.setSize(cardW, cardH);

      bg.on('pointerover', () => {
        bg.setFillStyle(0x1a1a3e, 1);
        bg.setStrokeStyle(3, this.getArenaColor(arena), 0.9);
      });
      bg.on('pointerout', () => {
        bg.setFillStyle(0x0d0d2b, 0.9);
        bg.setStrokeStyle(2, this.getArenaColor(arena), 0.5);
      });
      bg.on('pointerdown', () => {
        this.startBattle(arena.id);
      });
    });

    // Botón volver
    const backBtn = this.add.text(60, GAME_HEIGHT - 30, '← ATRÁS', {
      fontSize: '16px', color: '#e74c3c', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => this.scene.start('SelectionScene', { config: this.config, selections: this.selections }));
  }

  private getArenaColor(arena: ArenaDefinition): number {
    const colorMap: Record<string, number> = {
      clasica: 0x95a5a6,
      hielo: 0x85c1e9,
      lava: 0xe74c3c,
      electro: 0xf1c40f,
      laberinto: 0x8e44ad,
      ventosa: 0x1abc9c,
      diana: 0xe67e22,
      giratoria: 0x3498db,
    };
    return colorMap[arena.id] || 0x95a5a6;
  }

  private startBattle(arenaId: string): void {
    // Si es torneo y tenemos matchData, usar solo los participantes de esa match
    const battleSelections = this.matchSelections || this.selections;

    this.scene.start('BattleScene', {
      config: this.config,
      selections: battleSelections,
      arenaId,
      torneoSystem: this.torneoSystem,
      currentMatchData: this.currentMatchData,
      allSelections: this.selections,
      isTorneo: this.isTorneo,
    });
  }
}
